import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { FileText, Upload, Trash2, Download, Loader2, Sparkles, Salad } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import VaultSummaryDialog from '../../components/VaultSummaryDialog';

export default function MedicalVault() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [summaryFile, setSummaryFile] = useState(null);
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  const fetchFiles = () => api.get('/api/vault/files').then(res => setFiles(res.data.files || []));

  useEffect(() => {
    fetchFiles().finally(() => setLoading(false));
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`This file is ${(file.size / 1048576).toFixed(1)} MB. Please keep documents under 5 MB.`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const category = isAdmin ? 'admin' : 'personal';
      await api.post(`/api/vault/upload?category=${category}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded!');
      fetchFiles();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || (typeof d === 'string' ? d : 'Upload failed'));
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/api/vault/${fileId}`);
      toast.success('File deleted');
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot delete');
    }
  };

  const handleDownload = async (file) => {
    try {
      // stream=1 keeps the bytes on our origin — a browser XHR cannot follow
      // the presigned S3 redirect (S3 sends no CORS headers).
      const res = await api.get(`/api/vault/download/${file.id}?stream=1`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Download failed'); }
  };

  const adminFiles = files.filter(f => f.category === 'admin');
  const personalFiles = files.filter(f => f.category === 'personal');
  // V72 — diet plans shared by the nutritionist. Hers forever, with a clear header and
  // the date it was filed, kept apart from documents she uploaded herself.
  const dietPlans = files.filter(f => f.category === 'diet_plan');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Medical Vault</h1>
          <p className="text-muted-foreground mt-1">Securely store and manage your health documents</p>
        </div>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
          <Button asChild disabled={uploading}>
            <span>{uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Upload Document</span>
          </Button>
        </label>
      </div>

      <div className="space-y-6">
          {dietPlans.length > 0 && (
            <Card className="rounded-2xl border-emerald-200" data-testid="vault-diet-plans-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Salad className="h-4 w-4 text-emerald-600" />Diet Plans from your Nutritionist
                </CardTitle>
                <p className="text-sm text-muted-foreground">Yours to keep — these stay here even after a chat closes.</p>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                <div className="space-y-2">
                  {dietPlans.map(f => (
                    <div key={f.id} className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/50 px-3 py-2.5"
                      data-testid={`vault-diet-plan-${f.id}`}>
                      <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{f.title || 'Diet Plan'}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          Shared by {f.shared_by_name || 'your nutritionist'} · saved {formatDate(f.created_at)}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(f)}
                        data-testid={`vault-diet-plan-download-${f.id}`}>
                        <Download className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {adminFiles.length > 0 && (
            <Card className="rounded-2xl">
              <CardHeader><CardTitle className="font-heading text-lg">Resources from Soul Sanskar</CardTitle></CardHeader>
              <CardContent className="px-2 sm:px-6">
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead className="hidden md:table-cell">Type</TableHead>
                    <TableHead className="hidden sm:table-cell">Uploaded</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {adminFiles.map(f => (
                      <TableRow key={f.id}>
                        <TableCell className="flex items-center gap-2 max-w-[180px] sm:max-w-none truncate"><FileText className="h-4 w-4 text-primary flex-shrink-0" /><span className="truncate">{f.original_filename}</span></TableCell>
                        <TableCell className="hidden md:table-cell"><Badge variant="secondary">{f.content_type?.split('/')[1] || 'file'}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">{formatDate(f.created_at)}</TableCell>
                        <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handleDownload(f)}><Download className="h-3 w-3" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-2xl">
            <CardHeader><CardTitle className="font-heading text-lg">{isAdmin ? 'All Documents' : 'My Documents'}</CardTitle></CardHeader>
            <CardContent className="px-2 sm:px-6">
              {personalFiles.length === 0 ? (
                <div className="text-center py-8"><FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No documents uploaded yet</p></div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Document</TableHead>
                    {/* V14.1 — hide secondary columns on mobile so the action buttons
                        (incl. SoulAI Sparkles) stay visible without horizontal scroll */}
                    <TableHead className="hidden md:table-cell">Size</TableHead>
                    <TableHead className="hidden sm:table-cell">Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {personalFiles.map(f => (
                      <TableRow key={f.id}>
                        <TableCell className="flex items-center gap-2 max-w-[140px] sm:max-w-none truncate"><FileText className="h-4 w-4 text-blue-600 flex-shrink-0" /><span className="truncate">{f.original_filename}</span></TableCell>
                        <TableCell className="text-sm text-muted-foreground hidden md:table-cell">{(f.size / 1024).toFixed(0)} KB</TableCell>
                        <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">{formatDate(f.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {!isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                onClick={() => setSummaryFile(f)}
                                title="Analyze with SoulAI"
                                data-testid={`vault-analyze-${f.id}`}
                              >
                                <Sparkles className="h-3 w-3" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDownload(f)}><Download className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(f.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      <VaultSummaryDialog
        open={!!summaryFile}
        file={summaryFile}
        onClose={() => setSummaryFile(null)}
      />
    </div>
  );
}
